import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";

import { isGameAccessUnauthorized } from "../../features/game/gameApi";

import cls from "./AvatarPicker.module.scss";

export const DEFAULT_PROCESSED_SUBPATH = "images/poster.webp";

const AvatarPicker = forwardRef(function AvatarPicker(
  {
    value,
    onChange,
    presetOptions = [],
    allowUpload = true,
    gameId,
    gameAccessToken,
    presignAvatarUpload,
    onGameAccessExpired,
  },
  ref,
) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const sourceType =
    allowUpload && value?.sourceType === "upload" ? "upload" : "preset";

  const selectedPreset = useMemo(() => {
    const byKey = presetOptions.find(
      (option) => option.key === value?.avatarKey,
    );
    return byKey || presetOptions[0] || null;
  }, [presetOptions, value?.avatarKey]);

  const previewSrc =
    sourceType === "upload"
      ? value?.url || localPreview
      : selectedPreset?.src || "";

  const previewAlt =
    sourceType === "upload"
      ? "Uploaded avatar preview"
      : selectedPreset?.label || "Preset avatar";

  const cleanupPreview = useCallback((url) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore preview cleanup errors
    }
  }, []);

  const updatePreset = (option) => {
    if (!option) return;
    cleanupPreview(localPreview);
    setLocalPreview("");
    setFile(null);
    setUploading(false);
    onChange({
      sourceType: "preset",
      avatarKey: option.key,
      url: option.src,
      storageKey: "",
    });
  };

  const pickFile = (nextFile) => {
    if (!nextFile) return;

    if (!/^image\//.test(nextFile.type)) {
      toast.info("Please choose an image file.");
      return;
    }

    cleanupPreview(localPreview);
    const objectUrl = URL.createObjectURL(nextFile);
    setLocalPreview(objectUrl);
    setFile(nextFile);

    onChange({
      sourceType: "upload",
      avatarKey: "",
      url: objectUrl,
      storageKey: "",
    });
  };

  const handleFileInput = (event) => {
    pickFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    pickFile(event.dataTransfer?.files?.[0]);
  };

  const handleUpload = useCallback(async () => {
    if (!allowUpload || !gameId || !gameAccessToken) {
      toast.error("Game access expired. Please rejoin the game.");
      throw new Error("Game access expired");
    }

    if (value?.storageKey) {
      return {
        sourceType: "upload",
        avatarKey: "",
        url: value?.url || localPreview || "",
        storageKey: value.storageKey,
      };
    }

    if (!file) {
      toast.info("Choose an image first.");
      throw new Error("Missing avatar file");
    }

    setUploading(true);

    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const presign = await presignAvatarUpload(gameId, gameAccessToken, {
        ext,
        contentType: file.type || "application/octet-stream",
      });

      const processedBaseKey = presign?.processedBaseKey;
      const processedKey =
        presign?.processedKey ||
        `${processedBaseKey}/${DEFAULT_PROCESSED_SUBPATH}`;
      const uploadUrl = presign?.uploadUrl;

      if (!uploadUrl || !processedBaseKey || !processedKey) {
        throw new Error("Invalid presign response");
      }

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      const uploadedImage = {
        sourceType: "upload",
        avatarKey: "",
        url: value?.url || localPreview || "",
        storageKey: processedKey,
      };

      onChange(uploadedImage);
      return uploadedImage;
    } catch (error) {
      console.error("game avatar upload failed:", error);
      if (isGameAccessUnauthorized(error)) {
        onGameAccessExpired?.();
      } else {
        onChange({
          sourceType: "upload",
          avatarKey: "",
          url: localPreview,
          storageKey: "",
        });
        toast.error("Avatar upload failed. Please try again.");
      }
      throw error;
    } finally {
      setUploading(false);
    }
  }, [
    allowUpload,
    gameAccessToken,
    gameId,
    file,
    localPreview,
    onChange,
    onGameAccessExpired,
    presignAvatarUpload,
    value,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      async prepareForSubmit() {
        if (!allowUpload || sourceType !== "upload") {
          return value;
        }

        return handleUpload();
      },
    }),
    [allowUpload, sourceType, value, handleUpload],
  );

  useEffect(
    () => () => cleanupPreview(localPreview),
    [cleanupPreview, localPreview],
  );

  return (
    <section className={cls.picker}>
      <div className={cls.headerRow}>
        <div>
          <p className={cls.eyebrow}>Avatar</p>
          <h3 className={cls.title}>
            Choose your look or upload your own image.
          </h3>
        </div>

        {allowUpload ? (
          <div className={cls.modeToggle}>
            <button
              type="button"
              className={sourceType === "preset" ? cls.modeActive : ""}
              onClick={() => updatePreset(selectedPreset || presetOptions[0])}
            >
              Presets
            </button>
            <button
              type="button"
              className={sourceType === "upload" ? cls.modeActive : ""}
              onClick={() =>
                onChange({
                  sourceType: "upload",
                  avatarKey: "",
                  url:
                    value?.sourceType === "upload"
                      ? value?.url || localPreview || ""
                      : localPreview || "",
                  storageKey:
                    value?.sourceType === "upload"
                      ? value?.storageKey || ""
                      : "",
                })
              }
            >
              Upload
            </button>
          </div>
        ) : null}
      </div>

      <div className={cls.previewShell}>
        {previewSrc ? (
          <img className={cls.previewImage} src={previewSrc} alt={previewAlt} />
        ) : (
          <div className={cls.previewEmpty}>No avatar selected</div>
        )}
      </div>

      {sourceType === "preset" ? (
        <div className={cls.grid}>
          {presetOptions.map((option) => {
            const isActive = option.key === selectedPreset?.key;
            return (
              <button
                key={option.key}
                type="button"
                className={`${cls.optionCard} ${isActive ? cls.optionCardActive : ""}`}
                onClick={() => updatePreset(option)}
              >
                <img
                  src={option.src}
                  alt={option.label}
                  className={cls.optionImage}
                />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className={cls.uploadPanel}>
          <button
            type="button"
            className={cls.dropzone}
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(event) => event.preventDefault()}
          >
            <span className={cls.dropzoneTitle}>
              Drop an image here or tap to browse
            </span>
            <span className={cls.dropzoneHint}>
              Use a clear portrait photo for the same processing pipeline as
              member avatars.
            </span>
          </button>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileInput}
          />

          <p className={cls.uploadStatus}>
            {uploading
              ? "Uploading avatar now…"
              : value?.storageKey
                ? "Your uploaded avatar will be used when you create the player profile."
                : file
                  ? "Your image is ready. Tap the main create button to upload it and create the player profile together."
                  : "Upload your own avatar if you don’t want to use a preset."}
          </p>
        </div>
      )}
    </section>
  );
});

export default AvatarPicker;
